import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Plus, Trash2, X, Edit2, Check, ChevronDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import { AdminLayout } from './AdminLayout';
import {
  AdminSetupUser,
  SetupProfile,
  SetupRestaurant,
  SetupSection,
  SetupPlatform,
  SetupRevenueSource,
  getAdminSetupUsers,
  getAdminSetupProfile,
  createSetupPlatform,
  updateSetupPlatform,
  updateSetupPlatformSettings,
  deleteSetupPlatform,
  createSetupRevenueSource,
  updateSetupRevenueSource,
  deleteSetupRevenueSource,
} from '../../../services/admin/adminSetupService';
import { Platform } from '../../../types/Platforms';
import {
  RevenueSource,
  getRevenueSourceDisplayName,
} from '../../../types/RevenueSource';
import {
  RevenueSourceType,
  getRevenueSourceTypeDisplayName,
} from '../../../types/RevenueSourceType';

const PLATFORM_OPTIONS = Object.values(Platform).filter(v => typeof v === 'string') as string[];
const REVENUE_SOURCE_OPTIONS = Object.values(RevenueSource);
const REVENUE_SOURCE_TYPE_OPTIONS = Object.values(RevenueSourceType);

interface PlatformDraft {
  orderEmails: string[];
  orderClientNames: string[];
}

interface AddPlatformDraft {
  platformName: string;
  orderEmail: string;
  orderClientName: string;
}

interface RevenueSourceDraft {
  revenueSourceType: string;
  revenueSourceName: string;
  restaurantName: string;
  relevantSections: string[];
}

const ConfirmModal = ({
  open,
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={onCancel}>
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 className={cn(primaryText, 'text-lg', semiBoldText, 'mb-2')}>{title}</h3>
          <p className={cn(secondaryText, smallTextSize, 'mb-6')}>{message}</p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={onCancel}
              className={cn(
                'px-4 py-2 rounded-lg text-xs font-medium',
                'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200',
                'hover:bg-gray-200 dark:hover:bg-gray-600 transition'
              )}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 rounded-lg text-xs font-medium bg-red-600 dark:bg-red-600 text-white dark:text-white hover:bg-red-700 dark:hover:bg-red-700 transition"
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Editable list of string chips. Used for order_emails and order_client_names.
// Also reused by the AI Checker's AddRestaurantPlatformModal.
export const ChipListEditor = ({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
}) => {
  const [draft, setDraft] = useState('');

  const addChip = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (values.includes(trimmed)) {
      setDraft('');
      return;
    }
    onChange([...values, trimmed]);
    setDraft('');
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {values.length === 0 ? (
          <span className="text-xs italic text-gray-400 dark:text-textQuaternary-dark">empty</span>
        ) : (
          values.map(v => (
            <span
              key={v}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs"
            >
              {v}
              <button
                onClick={() => onChange(values.filter(x => x !== v))}
                className="hover:bg-emerald-200/60 dark:hover:bg-emerald-800 rounded-full p-0.5"
                aria-label="Remove"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))
        )}
      </div>
      <div className="flex gap-1">
        <input
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addChip();
            }
          }}
          placeholder={placeholder}
          className={cn(
            'flex-1 px-2 py-1 rounded-md border text-xs',
            'bg-white dark:bg-gray-800',
            'border-gray-200 dark:border-gray-700',
            'text-gray-900 dark:text-gray-100',
            'focus:outline-none focus:ring-1 focus:ring-purple-500 dark:focus:ring-purple-500'
          )}
        />
        <button
          onClick={addChip}
          className="px-2 py-1 rounded-md bg-purple-600 dark:bg-purple-600 text-white dark:text-white text-xs hover:bg-purple-700 dark:hover:bg-purple-700"
        >
          Add
        </button>
      </div>
    </div>
  );
};

/**
 * Van Gelder only: flips `settings.vangelderPakbonMode` on ONE restaurant_platform. Super-admin-only
 * on purpose — clients never see or choose their Van Gelder document world (see
 * z_ralph/tasks/1_new_tasks/spec-vangelder-bidfood-reconciliation.md), so this deliberately does NOT
 * live in the client-facing SavePlatformModal.
 *
 * Saves immediately on click (its own endpoint, merged server-side) rather than joining the row's
 * order-emails/client-names edit draft.
 */
const VangelderPakbonModeToggle = ({
  platformId,
  pakbonMode,
  onSaved,
}: {
  platformId: string;
  pakbonMode: boolean;
  onSaved: () => void;
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const flip = async () => {
    setSaving(true);
    try {
      await updateSetupPlatformSettings(platformId, { vangelderPakbonMode: !pakbonMode });
      onSaved();
    } catch (err) {
      alert(t('adminSetup.vangelderPakbonMode.saveFailed', { message: (err as Error).message }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/60">
      <span className={cn(secondaryText, 'text-xs font-medium')}>
        {t('adminSetup.vangelderPakbonMode.label')}
      </span>
      <button
        onClick={flip}
        disabled={saving}
        className={cn(
          'px-1.5 py-0.5 rounded-full text-[10px] font-medium transition disabled:opacity-50',
          pakbonMode
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/60'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
        )}
      >
        {pakbonMode
          ? t('adminSetup.vangelderPakbonMode.stateOn')
          : t('adminSetup.vangelderPakbonMode.stateOff')}
      </button>
      <span className="text-[10px] italic text-gray-500 dark:text-gray-400">
        {pakbonMode
          ? t('adminSetup.vangelderPakbonMode.on')
          : t('adminSetup.vangelderPakbonMode.off')}
      </span>
    </div>
  );
};

const PlatformRow = ({
  platform,
  onSaved,
  onDeleted,
}: {
  platform: SetupPlatform;
  onSaved: () => void;
  onDeleted: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PlatformDraft>({
    orderEmails: platform.orderEmails ?? [],
    orderClientNames: platform.orderClientNames ?? [],
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const emailsEmpty = !platform.orderEmails || platform.orderEmails.length === 0;
  const clientsEmpty = !platform.orderClientNames || platform.orderClientNames.length === 0;
  const hasWarning = emailsEmpty || clientsEmpty;

  const startEdit = () => {
    setDraft({
      orderEmails: platform.orderEmails ?? [],
      orderClientNames: platform.orderClientNames ?? [],
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateSetupPlatform(platform.id, draft);
      setEditing(false);
      onSaved();
    } catch (err) {
      alert(`Failed to save: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setSaving(true);
    try {
      await deleteSetupPlatform(platform.id);
      setConfirmDelete(false);
      onDeleted();
    } catch (err) {
      alert(`Failed to delete: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className={cn(primaryText, smallTextSize, semiBoldText)}>
            {platform.platformName}
          </span>
          <span
            className={cn(
              'px-1.5 py-0.5 rounded-full text-[10px]',
              platform.enabled
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            )}
          >
            {platform.enabled ? 'enabled' : 'disabled'}
          </span>
          {hasWarning && !editing && (
            <span
              className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"
              title="Order emails or client names are empty — incoming invoices will not match"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              <span className="text-[10px] font-medium">empty fields</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <button
                onClick={save}
                disabled={saving}
                className="p-1.5 rounded-md bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-50"
                aria-label="Save"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setEditing(false)}
                disabled={saving}
                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Cancel"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={startEdit}
                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Edit"
              >
                <Edit2 className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" />
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-950/30"
                aria-label="Delete"
              >
                <Trash2 className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
              </button>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-3">
          <div>
            <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>
              Order emails
            </div>
            <ChipListEditor
              values={draft.orderEmails}
              onChange={v => setDraft({ ...draft, orderEmails: v })}
              placeholder="orders@supplier.nl"
            />
          </div>
          <div>
            <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>
              Client names
            </div>
            <ChipListEditor
              values={draft.orderClientNames}
              onChange={v => setDraft({ ...draft, orderClientNames: v })}
              placeholder="Restaurant name on invoice"
            />
          </div>
        </div>
      ) : (
        <div className="space-y-1 text-xs">
          <div className="flex items-start gap-2">
            <span className={cn(secondaryText, 'font-medium min-w-[90px]')}>Order emails:</span>
            {emailsEmpty ? (
              <span className="italic text-gray-400 dark:text-textQuaternary-dark">empty</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {platform.orderEmails!.map(e => (
                  <span
                    key={e}
                    className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                  >
                    {e}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-start gap-2">
            <span className={cn(secondaryText, 'font-medium min-w-[90px]')}>Client names:</span>
            {clientsEmpty ? (
              <span className="italic text-gray-400 dark:text-textQuaternary-dark">empty</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {platform.orderClientNames!.map(n => (
                  <span
                    key={n}
                    className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                  >
                    {n}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {typeof platform.vangelderPakbonMode === 'boolean' && (
        <VangelderPakbonModeToggle
          platformId={platform.id}
          pakbonMode={platform.vangelderPakbonMode}
          onSaved={onSaved}
        />
      )}

      <ConfirmModal
        open={confirmDelete}
        title="Delete platform"
        message={`Delete "${platform.platformName}" from this section? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
};

const AddPlatformForm = ({
  sectionId,
  existingPlatformNames,
  onAdded,
  onCancel,
}: {
  sectionId: string;
  existingPlatformNames: string[];
  onAdded: () => void;
  onCancel: () => void;
}) => {
  const [draft, setDraft] = useState<AddPlatformDraft>({
    platformName: '',
    orderEmail: '',
    orderClientName: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!draft.platformName) {
      alert('Pick a platform.');
      return;
    }
    setSaving(true);
    try {
      await createSetupPlatform({
        restaurantSectionId: sectionId,
        platformName: draft.platformName,
        orderEmails: draft.orderEmail.trim() ? [draft.orderEmail.trim()] : undefined,
        orderClientNames: draft.orderClientName.trim() ? [draft.orderClientName.trim()] : undefined,
      });
      onAdded();
    } catch (err) {
      alert(`Failed to create: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/40 dark:bg-purple-900/10 space-y-2">
      <div className={cn(primaryText, smallTextSize, semiBoldText)}>Add platform</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <select
          value={draft.platformName}
          onChange={e => setDraft({ ...draft, platformName: e.target.value })}
          className={cn(
            'px-2 py-1 rounded-md border text-xs',
            'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'
          )}
        >
          <option value="">Pick platform…</option>
          {PLATFORM_OPTIONS
            .filter(p => !existingPlatformNames.includes(p))
            .map(p => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
        </select>
        <input
          type="text"
          placeholder="First order email (optional)"
          value={draft.orderEmail}
          onChange={e => setDraft({ ...draft, orderEmail: e.target.value })}
          className={cn(
            'px-2 py-1 rounded-md border text-xs',
            'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'
          )}
        />
        <input
          type="text"
          placeholder="First client name (optional)"
          value={draft.orderClientName}
          onChange={e => setDraft({ ...draft, orderClientName: e.target.value })}
          className={cn(
            'px-2 py-1 rounded-md border text-xs',
            'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'
          )}
        />
      </div>
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1 rounded-md text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={saving}
          className="px-3 py-1 rounded-md text-xs bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-50"
        >
          {saving ? 'Adding…' : 'Add'}
        </button>
      </div>
    </div>
  );
};

const SectionBlock = ({
  section,
  onChanged,
}: {
  section: SetupSection;
  onChanged: () => void;
}) => {
  const [adding, setAdding] = useState(false);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50/50 dark:bg-gray-900/30">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className={cn(primaryText, smallTextSize, semiBoldText)}>{section.name}</div>
          <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>
            {section.restaurantSectionType}
          </div>
        </div>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Plus className="w-3 h-3" />
            Add platform
          </button>
        )}
      </div>

      {section.platforms.length === 0 && !adding && (
        <p className="text-xs italic text-gray-500 dark:text-gray-400">No platforms.</p>
      )}

      <div className="space-y-2">
        {section.platforms.map(p => (
          <PlatformRow
            key={p.id}
            platform={p}
            onSaved={onChanged}
            onDeleted={onChanged}
          />
        ))}

        {adding && (
          <AddPlatformForm
            sectionId={section.id}
            existingPlatformNames={section.platforms.map(p => p.platformName)}
            onAdded={() => {
              setAdding(false);
              onChanged();
            }}
            onCancel={() => setAdding(false)}
          />
        )}
      </div>
    </div>
  );
};

const RevenueSourceRow = ({
  source,
  availableSectionNames,
  onSaved,
  onDeleted,
}: {
  source: SetupRevenueSource;
  availableSectionNames: string[];
  onSaved: () => void;
  onDeleted: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RevenueSourceDraft>({
    revenueSourceType: source.revenueSourceType,
    revenueSourceName: source.revenueSourceName,
    restaurantName: source.restaurantName,
    relevantSections: source.relevantSections,
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const startEdit = () => {
    setDraft({
      revenueSourceType: source.revenueSourceType,
      revenueSourceName: source.revenueSourceName,
      restaurantName: source.restaurantName,
      relevantSections: source.relevantSections,
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateSetupRevenueSource(source.id, draft);
      setEditing(false);
      onSaved();
    } catch (err) {
      alert(`Failed to save: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setSaving(true);
    try {
      await deleteSetupRevenueSource(source.id);
      setConfirmDelete(false);
      onDeleted();
    } catch (err) {
      alert(`Failed to delete: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (name: string) => {
    if (draft.relevantSections.includes(name)) {
      setDraft({ ...draft, relevantSections: draft.relevantSections.filter(s => s !== name) });
    } else {
      setDraft({ ...draft, relevantSections: [...draft.relevantSections, name] });
    }
  };

  return (
    <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn(primaryText, smallTextSize, semiBoldText)}>
            {getRevenueSourceDisplayName(source.revenueSourceName as RevenueSource)}
          </span>
          <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
            {getRevenueSourceTypeDisplayName(source.revenueSourceType as RevenueSourceType)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <button
                onClick={save}
                disabled={saving}
                className="p-1.5 rounded-md bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-50"
                aria-label="Save"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setEditing(false)}
                disabled={saving}
                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Cancel"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={startEdit}
                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Edit"
              >
                <Edit2 className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" />
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-950/30"
                aria-label="Delete"
              >
                <Trash2 className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
              </button>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-1')}>Type</div>
              <select
                value={draft.revenueSourceType}
                onChange={e => setDraft({ ...draft, revenueSourceType: e.target.value })}
                className="w-full px-2 py-1 rounded-md border text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
              >
                {REVENUE_SOURCE_TYPE_OPTIONS.map(t => (
                  <option key={t} value={t}>
                    {getRevenueSourceTypeDisplayName(t)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-1')}>Source</div>
              <select
                value={draft.revenueSourceName}
                onChange={e => setDraft({ ...draft, revenueSourceName: e.target.value })}
                className="w-full px-2 py-1 rounded-md border text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
              >
                {REVENUE_SOURCE_OPTIONS.map(s => (
                  <option key={s} value={s}>
                    {getRevenueSourceDisplayName(s)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-1')}>Restaurant name</div>
              <input
                type="text"
                value={draft.restaurantName}
                onChange={e => setDraft({ ...draft, restaurantName: e.target.value })}
                className="w-full px-2 py-1 rounded-md border text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          <div>
            <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-1')}>Relevant sections</div>
            <div className="flex flex-wrap gap-1">
              {availableSectionNames.length === 0 && (
                <span className="text-xs italic text-gray-400 dark:text-textQuaternary-dark">No sections on this restaurant.</span>
              )}
              {availableSectionNames.map(name => {
                const active = draft.relevantSections.includes(name);
                return (
                  <button
                    key={name}
                    onClick={() => toggleSection(name)}
                    className={cn(
                      'px-2 py-0.5 rounded-md text-xs border transition',
                      active
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    )}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <span className={cn(secondaryText, 'font-medium min-w-[110px]')}>Restaurant name:</span>
            <span className={primaryText}>{source.restaurantName}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className={cn(secondaryText, 'font-medium min-w-[110px]')}>Sections:</span>
            {source.relevantSections.length === 0 ? (
              <span className="italic text-gray-400 dark:text-textQuaternary-dark">none</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {source.relevantSections.map(s => (
                  <span
                    key={s}
                    className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                  >
                    {s}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirmDelete}
        title="Delete revenue source"
        message="Delete this revenue source? This cannot be undone."
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
};

const AddRevenueSourceForm = ({
  restaurantId,
  defaultRestaurantName,
  availableSectionNames,
  onAdded,
  onCancel,
}: {
  restaurantId: string;
  defaultRestaurantName: string;
  availableSectionNames: string[];
  onAdded: () => void;
  onCancel: () => void;
}) => {
  const [draft, setDraft] = useState<RevenueSourceDraft>({
    revenueSourceType: REVENUE_SOURCE_TYPE_OPTIONS[0],
    revenueSourceName: REVENUE_SOURCE_OPTIONS[0],
    restaurantName: defaultRestaurantName,
    relevantSections: [],
  });
  const [saving, setSaving] = useState(false);

  const toggleSection = (name: string) => {
    if (draft.relevantSections.includes(name)) {
      setDraft({ ...draft, relevantSections: draft.relevantSections.filter(s => s !== name) });
    } else {
      setDraft({ ...draft, relevantSections: [...draft.relevantSections, name] });
    }
  };

  const submit = async () => {
    if (!draft.restaurantName.trim()) {
      alert('Restaurant name is required.');
      return;
    }
    setSaving(true);
    try {
      await createSetupRevenueSource({ restaurantId, ...draft });
      onAdded();
    } catch (err) {
      alert(`Failed to create: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/40 dark:bg-purple-900/10 space-y-2">
      <div className={cn(primaryText, smallTextSize, semiBoldText)}>Add revenue source</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <select
          value={draft.revenueSourceType}
          onChange={e => setDraft({ ...draft, revenueSourceType: e.target.value })}
          className="px-2 py-1 rounded-md border text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
        >
          {REVENUE_SOURCE_TYPE_OPTIONS.map(t => (
            <option key={t} value={t}>
              {getRevenueSourceTypeDisplayName(t)}
            </option>
          ))}
        </select>
        <select
          value={draft.revenueSourceName}
          onChange={e => setDraft({ ...draft, revenueSourceName: e.target.value })}
          className="px-2 py-1 rounded-md border text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
        >
          {REVENUE_SOURCE_OPTIONS.map(s => (
            <option key={s} value={s}>
              {getRevenueSourceDisplayName(s)}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Restaurant name on POS"
          value={draft.restaurantName}
          onChange={e => setDraft({ ...draft, restaurantName: e.target.value })}
          className="px-2 py-1 rounded-md border text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>
      <div>
        <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-1')}>
          Relevant sections
        </div>
        <div className="flex flex-wrap gap-1">
          {availableSectionNames.length === 0 && (
            <span className="text-xs italic text-gray-400 dark:text-textQuaternary-dark">No sections on this restaurant.</span>
          )}
          {availableSectionNames.map(name => {
            const active = draft.relevantSections.includes(name);
            return (
              <button
                key={name}
                onClick={() => toggleSection(name)}
                className={cn(
                  'px-2 py-0.5 rounded-md text-xs border transition',
                  active
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                )}
              >
                {name}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1 rounded-md text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={saving}
          className="px-3 py-1 rounded-md text-xs bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-50"
        >
          {saving ? 'Adding…' : 'Add'}
        </button>
      </div>
    </div>
  );
};

const RestaurantBlock = ({
  restaurant,
  onChanged,
  initiallyExpanded = false,
}: {
  restaurant: SetupRestaurant;
  onChanged: () => void;
  initiallyExpanded?: boolean;
}) => {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [addingRevenueSource, setAddingRevenueSource] = useState(false);
  const sectionNames = restaurant.sections.map(s => s.name);

  const platformCount = restaurant.sections.reduce((acc, s) => acc + s.platforms.length, 0);
  const hasEmptyPlatform = restaurant.sections.some(s =>
    s.platforms.some(
      p =>
        !p.orderEmails ||
        p.orderEmails.length === 0 ||
        !p.orderClientNames ||
        p.orderClientNames.length === 0
    )
  );

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/60 transition text-left"
      >
        <div>
          <div className="flex items-center gap-2">
            <h3 className={cn(primaryText, 'text-base', semiBoldText)}>{restaurant.name}</h3>
            {hasEmptyPlatform && (
              <span
                className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"
                title="One or more platforms have empty order emails or client names"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
              </span>
            )}
          </div>
          <div className={cn(secondaryText, 'text-xs')}>
            {restaurant.sections.length} sections · {platformCount} platforms · {restaurant.revenueSources.length} revenue sources
          </div>
        </div>
        <ChevronDown
          className={cn('w-4 h-4 text-gray-400 dark:text-textQuaternary-dark transition-transform', expanded && 'rotate-180')}
        />
      </button>

      {!expanded && null}
      {expanded && (
      <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4">
      {/* Platforms by section */}
      <div className="mb-5">
        <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-2', semiBoldText)}>
          Platforms
        </div>
        {restaurant.sections.length === 0 ? (
          <p className="text-xs italic text-gray-500 dark:text-gray-400">No sections.</p>
        ) : (
          <div className="space-y-3">
            {restaurant.sections.map(s => (
              <SectionBlock key={s.id} section={s} onChanged={onChanged} />
            ))}
          </div>
        )}
      </div>

      {/* Revenue sources */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className={cn(secondaryText, 'text-xs uppercase tracking-wide', semiBoldText)}>
            Revenue sources
          </div>
          {!addingRevenueSource && (
            <button
              onClick={() => setAddingRevenueSource(true)}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Plus className="w-3 h-3" />
              Add revenue source
            </button>
          )}
        </div>
        {restaurant.revenueSources.length === 0 && !addingRevenueSource && (
          <p className="text-xs italic text-gray-500 dark:text-gray-400">No revenue sources.</p>
        )}
        <div className="space-y-2">
          {restaurant.revenueSources.map(rs => (
            <RevenueSourceRow
              key={rs.id}
              source={rs}
              availableSectionNames={sectionNames}
              onSaved={onChanged}
              onDeleted={onChanged}
            />
          ))}
          {addingRevenueSource && (
            <AddRevenueSourceForm
              restaurantId={restaurant.id}
              defaultRestaurantName={restaurant.name}
              availableSectionNames={sectionNames}
              onAdded={() => {
                setAddingRevenueSource(false);
                onChanged();
              }}
              onCancel={() => setAddingRevenueSource(false)}
            />
          )}
        </div>
      </div>
      </div>
      )}
    </div>
  );
};

const AdminSetupPage = () => {
  const [searchParams] = useSearchParams();
  const requestedUserId = searchParams.get('userId');
  const requestedRestaurantId = searchParams.get('restaurantId');
  const [users, setUsers] = useState<AdminSetupUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [profileByUser, setProfileByUser] = useState<Record<string, SetupProfile>>({});
  const [profileLoading, setProfileLoading] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await getAdminSetupUsers();
        setUsers(list);
      } catch (err) {
        console.error('Failed to load admin users for setup:', err);
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, []);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      u =>
        u.email.toLowerCase().includes(q) ||
        (u.name && u.name.toLowerCase().includes(q))
    );
  }, [users, searchQuery]);

  const loadProfile = useCallback(async (userId: string) => {
    setProfileLoading(userId);
    try {
      const profile = await getAdminSetupProfile(userId);
      setProfileByUser(prev => ({ ...prev, [userId]: profile }));
    } catch (err) {
      console.error('Failed to load setup profile:', err);
    } finally {
      setProfileLoading(null);
    }
  }, []);

  useEffect(() => {
    const requestedUser = users.find(user => user.id === requestedUserId);
    if (!requestedUser) return;
    setSearchQuery(requestedUser.email);
    setExpandedUserId(requestedUser.id);
    void loadProfile(requestedUser.id);
  }, [requestedUserId, users, loadProfile]);

  const handleToggleExpand = (userId: string) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(userId);
    if (!profileByUser[userId]) {
      loadProfile(userId);
    }
  };

  const refreshExpanded = () => {
    if (expandedUserId) loadProfile(expandedUserId);
  };

  if (loadingUsers) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className={cn(secondaryText)}>Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <h2 className={cn(primaryText, 'text-lg', semiBoldText, 'mb-2')}>
          Customer Setup
        </h2>
        <p className={cn(secondaryText, smallTextSize)}>
          Inspect and adjust customer profile configuration: supplier platforms (order emails, client names) and revenue sources per restaurant.
        </p>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-textQuaternary-dark"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search by email or name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={cn(
              'w-full pl-10 pr-4 py-2 rounded-lg border',
              'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
              'text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500',
              'focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-500 focus:border-transparent',
              smallTextSize
            )}
          />
        </div>
      </div>

      <div className="space-y-3">
        {filteredUsers.length === 0 ? (
          <div className={cn(cardClass, 'p-8 text-center')}>
            <p className={secondaryText}>No users found</p>
          </div>
        ) : (
          filteredUsers.map(user => {
            const isExpanded = expandedUserId === user.id;
            const profile = profileByUser[user.id];
            return (
              <div key={user.id} className={cn(cardClass, 'overflow-hidden')}>
                <button
                  onClick={() => handleToggleExpand(user.id)}
                  className={cn(
                    'w-full px-4 py-3 flex items-center justify-between',
                    'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center',
                        'bg-purple-100 dark:bg-purple-900/30',
                        'text-purple-600 dark:text-purple-400',
                        smallTextSize,
                        semiBoldText
                      )}
                    >
                      {(user.name || user.email)[0].toUpperCase()}
                    </div>
                    <div>
                      <div className={cn(primaryText, smallTextSize, semiBoldText)}>
                        {user.name || user.email}
                      </div>
                      <div className={cn(secondaryText, 'text-xs')}>{user.email}</div>
                    </div>
                  </div>

                  <svg
                    className={cn(
                      'w-4 h-4 text-gray-400 dark:text-textQuaternary-dark transition-transform',
                      isExpanded && 'rotate-180'
                    )}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4 space-y-4">
                    {profileLoading === user.id ? (
                      <div className={cn(secondaryText, 'text-xs')}>Loading profile…</div>
                    ) : profile ? (
                      profile.restaurants.length === 0 ? (
                        <p className="text-xs italic text-gray-500 dark:text-gray-400">
                          This user has no restaurants.
                        </p>
                      ) : (
                        profile.restaurants.map(r => (
                          <RestaurantBlock
                            key={r.id}
                            restaurant={r}
                            initiallyExpanded={r.id === requestedRestaurantId}
                            onChanged={refreshExpanded}
                          />
                        ))
                      )
                    ) : (
                      <div className={cn(secondaryText, 'text-xs')}>No data.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSetupPage;
